const SOURCE_CODES = {
  default: "0",
  hub: "2",
} as const;

export type SiteSource = keyof typeof SOURCE_CODES;

const REVERSE_SOURCE_CODES: Record<string, SiteSource> = {
  "0": "default",
  "2": "hub",
} as const;

const MAX_UNIQUE_LENGTH = 31;
const URL_SAFE_PATTERN = /^[a-zA-Z0-9_-]+$/;

declare const SiteIdBrand: unique symbol;

/**
 * A unique identifier for an A3S Site.
 *
 * Structure:
 * - Source: the source system ('default' or 'hub')
 * - Unique: a URL-safe string (1-31 chars, [a-zA-Z0-9_-])
 */
export type SiteId = string & { readonly [SiteIdBrand]: "SiteId" };

export class SiteIdError extends Error {
  constructor(
    public readonly kind: "OutOfRange" | "Format" | "InvalidSource",
    message: string,
  ) {
    super(message);
  }
}

/**
 * Constructs a SiteId from its component parts or throws a SiteIdError.
 *
 * @param source - The source system ('default' or 'hub')
 * @param unique - URL-safe string (1-31 chars, [a-zA-Z0-9_-])
 */
const fromParts = (source: SiteSource, unique: string): SiteId => {
  if (!(source in SOURCE_CODES)) {
    throw new SiteIdError(
      "InvalidSource",
      `Source ${source} is not valid. Expected one of: ${Object.keys(SOURCE_CODES).join(", ")}.`,
    );
  }

  if (typeof unique !== "string" || unique.length === 0) {
    throw new SiteIdError("OutOfRange", `Unique string cannot be empty.`);
  }

  if (unique.length > MAX_UNIQUE_LENGTH) {
    throw new SiteIdError(
      "OutOfRange",
      `Unique string ${unique} exceeds maximum length. Expected at most ${String(MAX_UNIQUE_LENGTH)} characters, got ${String(unique.length)}.`,
    );
  }

  if (!URL_SAFE_PATTERN.test(unique)) {
    throw new SiteIdError(
      "Format",
      `Unique string ${unique} contains invalid characters. Expected only [a-zA-Z0-9_-].`,
    );
  }

  const sourceCode = SOURCE_CODES[source];
  return `S${sourceCode}-${unique}` as SiteId;
};

/**
 * Canonical encoding is 'S' + source code + '-' + unique string.
 */
const toString = (id: SiteId): string => {
  return id as string;
};

/**
 * Parses a SiteId from canonical encoding or throws a SiteIdError.
 * (Accepts `/^S[02]-[a-zA-Z0-9_-]{1,31}$/`.)
 */
const fromString = (str: string): SiteId => {
  if (typeof str !== "string" || str.length < 4) {
    throw new SiteIdError("Format", `String ${str} is not a valid SiteId. Expected format: S{source}-{unique}.`);
  }

  if (str.charAt(0) !== "S") {
    throw new SiteIdError("Format", `String ${str} is not a valid SiteId. Expected to start with 'S'.`);
  }

  const sourceCode = str.charAt(1);
  if (!(sourceCode in REVERSE_SOURCE_CODES)) {
    throw new SiteIdError(
      "InvalidSource",
      `String ${str} has invalid source code '${sourceCode}'. Expected one of: ${Object.keys(REVERSE_SOURCE_CODES).join(", ")}.`,
    );
  }

  if (str.charAt(2) !== "-") {
    throw new SiteIdError("Format", `String ${str} is not a valid SiteId. Expected '-' separator at position 2.`);
  }

  const unique = str.slice(3);

  if (unique.length === 0) {
    throw new SiteIdError("OutOfRange", `String ${str} has empty unique part.`);
  }

  if (unique.length > MAX_UNIQUE_LENGTH) {
    throw new SiteIdError(
      "OutOfRange",
      `String ${str} has unique part that exceeds maximum length. Expected at most ${String(MAX_UNIQUE_LENGTH)} characters, got ${String(unique.length)}.`,
    );
  }

  if (!URL_SAFE_PATTERN.test(unique)) {
    throw new SiteIdError(
      "Format",
      `String ${str} has unique part with invalid characters. Expected only [a-zA-Z0-9_-].`,
    );
  }

  return str as SiteId;
};

/**
 * Extracts the component parts from a SiteId.
 *
 * @param id - The SiteId to decompose
 * @returns An object with source and unique properties
 */
const toParts = (id: SiteId): { source: SiteSource; unique: string } => {
  const str = id as string;
  const sourceCode = str.charAt(1);
  const source = REVERSE_SOURCE_CODES[sourceCode];
  const unique = str.slice(3); // Skip 'S', source code, and '-'

  if (!source) {
    throw new SiteIdError("InvalidSource", `SiteId ${str} has invalid source code '${sourceCode}'.`);
  }

  return { source, unique };
};

/**
 * Template tag for interpolating SiteId values in template literals.
 * Example: f`Site${siteId}Config` → "SiteS2-my-siteConfig"
 */
const f = (strings: TemplateStringsArray, ...values: SiteId[]): string =>
  (strings[0] ?? "") + values.map((v, i) => toString(v) + (strings[i + 1] ?? "")).join("");

export const SiteId = {
  fromParts,
  toString,
  fromString,
  toParts,
  f,
} as const;
